import type { AstPredicate, KendoOp } from '../tools/render/types'

import type { IocItem } from './ioc.types'

// Only true PE images route to process_path (ImageFileName) matches.
// Scripts, documents, and other non-loadable types fall through to
// process_cmdline contains — they typically show up as CommandLine
// arguments, not as the process image name.
const EXECUTABLE_EXTS = new Set<string>(['.exe'])

// Loadable modules / drivers / shared libraries — these route to the
// `object_loaded` CCM field so image_load queries can match the correct
// field (ImageFileName / modload / module.path) in each tool instead of
// leaning on per-tool process_path → image-field workarounds.
const LOADABLE_EXTS = new Set<string>([
  '.dll', // Windows DLL
  '.sys', // Windows kernel driver
  '.ocx', // Windows ActiveX control
  '.drv', // Windows driver
  '.cpl', // Windows control panel applet
  '.so', // Linux shared object
  '.dylib', // macOS dynamic library
])

// Strip per-user directory prefixes so the same IOC works across users.
// Matches:
//   C:\Users\<name>\...              (any drive, slash mix)
//   C:\Documents and Settings\<name>\...
//   /home/<name>/...                  (linux)
//   /Users/<name>/...                 (macOS)
// When stripped the caller should switch to `contains` — the remaining path is
// an interior fragment, not a suffix.
const USER_DIR_PREFIX =
  /^(?:[A-Za-z]:[\\/](?:Users|Documents and Settings)[\\/][^\\/]+[\\/]|\/(?:home|Users)\/[^/]+\/)/

function stripUserDir(value: string): { value: string; stripped: boolean } {
  const m = value.match(USER_DIR_PREFIX)
  if (!m) return { value, stripped: false }
  return { value: value.slice(m[0].length), stripped: true }
}

// A value "looks like a path" if it carries any path separator or drive-letter
// colon. These values route to process_path (full-path field on most tools);
// bare tokens route to process_name (basename field). Tools whose *_path
// mapping points at a basename-only field should list `process_path` in their
// per-category `unsupportedFields` so these predicates skip those categories
// instead of producing a guaranteed-empty search.
function looksLikePath(value: string): boolean {
  return /[\\/:]/.test(value)
}

export function mapIocToPredicates(ioc: IocItem): AstPredicate[] {
  const v = ioc.value

  switch (ioc.type) {
    case 'MD5':
      return [pred('md5', 'eq', v)]
    case 'SHA256':
      return [pred('sha256', 'eq', v)]

    case 'FileName': {
      const { value: normalized, stripped } = stripUserDir(v)
      // Stripped user-dir → the remainder is an interior fragment, so we
      // switch to `contains`. Bare IOCs keep the original `endswith` semantics.
      const fileOp: KendoOp = stripped ? 'contains' : 'endswith'
      const isPath = looksLikePath(normalized)
      const out: AstPredicate[] = []

      // target_path covers file reads/writes. It maps to a full-path field on
      // every supported tool, so endswith/contains both work regardless of
      // whether the IOC is a basename or a full path.
      out.push(pred('target_path', fileOp, normalized))

      if (looksExecutable(normalized)) {
        // Route to process_name for bare basenames, process_path for paths.
        // Tools whose process_path mapping collapses to a basename-only field
        // (e.g. CrowdStrike LogScale's ContextBaseFileName on network_connection
        // / dns) should drop process_path via `unsupportedFields`.
        out.push(
          pred(isPath ? 'process_path' : 'process_name', fileOp, normalized),
        )
      } else if (looksLoadable(normalized)) {
        // Loadable modules route to object_loaded — the correct field for
        // image_load events in every tool (ImageFileName / modload /
        // module.path / etc.). This avoids reusing process_path as a proxy.
        out.push(pred('object_loaded', fileOp, normalized))
      } else {
        out.push(pred('process_cmdline', 'contains', normalized))
      }
      return out
    }

    case 'MaliciousURL':
    case 'URL':
    case 'ActorOtherWebsite':
      return [pred('url', 'contains', v)]

    case 'MaliciousDomain':
    case 'Domain':
    case 'ActorDomain':
      return [pred('domain', 'eq', v), pred('dns_query', 'eq', v)]

    case 'IPAddress':
      return [pred('dest_ip', 'eq', v), pred('dns_answer', 'eq', v)]

    default:
      return []
  }
}

export function looksExecutable(name: string): boolean {
  const dot = name.lastIndexOf('.')
  if (dot === -1) return false
  const ext = name.slice(dot).toLowerCase()
  return EXECUTABLE_EXTS.has(ext)
}

export function looksLoadable(name: string): boolean {
  const dot = name.lastIndexOf('.')
  if (dot === -1) return false
  const ext = name.slice(dot).toLowerCase()
  return LOADABLE_EXTS.has(ext)
}

function pred(field: string, op: KendoOp, value: string): AstPredicate {
  const val = typeof value === 'string' && value.length > 0 ? [value] : []
  return { kind: 'pred', field, op, value: val, valueType: 'string' }
}
